import { Component, ElementRef, HostBinding, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSort, MatPaginator, MatTableDataSource } from '@angular/material';

import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { first, map, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { Chart } from 'chart.js';

import {
    ApplicationService,
    PersonInfo,
    CategoryInfo,
    UpsertCategoryInfo,
    RequestSummaryStatistics,
    DashboardData,
} from 'services/application/application.service';
import { DialogService } from 'services/dialogs/dialogs.service';
import { ErrorService } from 'services/error/error.service';
import { ScreenSizeService } from 'services/screensize/screensize.service';
import { SplashService } from 'services/splash/splash.service';
import { UserInfo, UserService } from 'services/user/user.service';
import { UnsavedChanges, UnsavedChangesService } from 'routes/routes.guard';

import { TitleCasePipe } from 'pipes/titlecase.pipe';
import { Compiler_compileModuleAndAllComponentsAsync__POST_R3__ } from '@angular/core/src/linker/compiler';

@Component({
    selector: 'app-dashboard',
    styleUrls: ['dashboard.component.scss'],
    templateUrl: 'dashboard.component.html',
})
export class DashboardComponent implements OnInit, OnDestroy {
    @HostBinding('class.smallsize') SmallDisplay: boolean;

    private _subrelease: Subject<void> = new Subject<void>();

    AllStatistics: DashboardData;
    BoardMembersChart: Chart;
    BoardMembersSummary: RequestSummaryStatistics[];
    BoardMembersTotals: RequestSummaryStatistics;
    BoardStatistics: RequestSummaryStatistics;
    CategoriesSummary: RequestSummaryStatistics[];
    CategoriesTotals: RequestSummaryStatistics;
    CategoriesChart: Chart;
    ConstituentStatistics: RequestSummaryStatistics;
    PrioritiesChart: Chart;
    LastUpdated: Date;
    User: UserInfo;
    ViewReady: boolean;

    constructor(
        private appService: ApplicationService,
        private dialogService: DialogService,
        private errorService: ErrorService,
        private fb: FormBuilder,
        private screenSizeService: ScreenSizeService,
        private splashService: SplashService,
        private titleCasePipe: TitleCasePipe,
        private userService: UserService
    ) {}

    ngOnDestroy() {
        this._subrelease.next();
        this._subrelease.complete();
    }

    ngOnInit() {
        const self = this;

        self.AllStatistics = null;
        self.BoardMembersSummary = [];
        self.BoardMembersTotals = null;
        self.BoardStatistics = null;
        self.CategoriesSummary = [];
        self.CategoriesTotals = null;
        self.ConstituentStatistics = null;
        self.LastUpdated = null;
        self.User = null;
        self.ViewReady = false;

        // Subscribe to changes in the screen size.
        self.screenSizeService.ScreenSizeSub.pipe(takeUntil(self._subrelease)).subscribe(size => {
            self.SmallDisplay = size.IsSmallSize;
        });

        self.SmallDisplay = self.screenSizeService.ScreenSize.IsSmallSize; // Initial setting

        // Wait for the first non-null UserInfo object, to begin component life cycle.
        self.userService.UserSubject.pipe(
            takeUntil(self._subrelease),
            first(_u => _u && !!_u.AuthToken)
        ).subscribe(
            _user => {
                self.User = _user;

                // Set up constant user monitoring
                self.userService.UserSubject.pipe(takeUntil(self._subrelease)).subscribe(
                    user => {
                        self.User = user;
                    },
                    error => self.errorService.HandleServiceError(error)
                );

                // Grab the information needed for the component
                combineLatest(self.appService.GetDashboardSummaryStats(self.User.AuthToken, self.User.CurrentRole))
                    .pipe(
                        takeUntil(self._subrelease),
                        map(_packet => {
                            return {
                                Stats: _packet[0],
                            };
                        })
                    )
                    .subscribe(
                        packet => {
                            self.AllStatistics = packet.Stats;
                            self.BoardMembersSummary = (self.AllStatistics && self.AllStatistics.BoardMembersSummary) || [];

                            // Setup the totals entry for board members
                            self.BoardMembersTotals = {
                                CancelledCount: self.BoardMembersSummary.reduce((p, c) => p + c.CancelledCount, 0),
                                ClosedNonInfoCount: self.BoardMembersSummary.reduce((p, c) => p + c.ClosedNonInfoCount, 0),
                                AllUnassigned: self.BoardMembersSummary.reduce((p, c) => p + c.AllUnassigned, 0),
                                AllUnanswered: self.BoardMembersSummary.reduce((p, c) => p + c.AllUnanswered, 0),
                                AllAnswered: self.BoardMembersSummary.reduce((p, c) => p + c.AllAnswered, 0),
                                AllComplete: self.BoardMembersSummary.reduce((p, c) => p + c.AllComplete, 0),
                                AllJobTime: self.BoardMembersSummary.reduce((p, c) => p + c.AllJobTime, 0),
                                TotalCount: self.BoardMembersSummary.reduce((p, c) => p + c.TotalCount, 0),
                            };

                            self.BoardMembersTotals = {
                                ...self.BoardMembersTotals,
                                CancelledPercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.CancelledCount || 0) * 100.0) / self.BoardMembersTotals.TotalCount,
                                ClosedNonInfoPercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.ClosedNonInfoCount || 0) * 100.0) /
                                          self.BoardMembersTotals.TotalCount,
                                AllUnassignedPercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.AllUnassigned || 0) * 100.0) / self.BoardMembersTotals.TotalCount,
                                AllUnansweredPercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.AllUnanswered || 0) * 100.0) / self.BoardMembersTotals.TotalCount,
                                AllAnsweredPercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.AllAnswered || 0) * 100.0) / self.BoardMembersTotals.TotalCount,
                                AllCompletePercent:
                                    self.BoardMembersTotals.TotalCount === 0
                                        ? 0
                                        : ((self.BoardMembersTotals.AllComplete || 0) * 100.0) / self.BoardMembersTotals.TotalCount,
                            };

                            self.BoardStatistics = ((self.AllStatistics && self.AllStatistics.RequestsSummary) || []).find(
                                s => s.RequestType === 'Board'
                            );

                            self.CategoriesSummary = self.AllStatistics && self.AllStatistics.CategoriesSummary;

                            // Setup the totals entry for categories
                            self.CategoriesTotals = {
                                CancelledCount: self.CategoriesSummary.reduce((p, c) => p + c.CancelledCount, 0),
                                ClosedNonInfoCount: self.CategoriesSummary.reduce((p, c) => p + c.ClosedNonInfoCount, 0),
                                AllUnassigned: self.CategoriesSummary.reduce((p, c) => p + c.AllUnassigned, 0),
                                AllUnanswered: self.CategoriesSummary.reduce((p, c) => p + c.AllUnanswered, 0),
                                AllAnswered: self.CategoriesSummary.reduce((p, c) => p + c.AllAnswered, 0),
                                AllComplete: self.CategoriesSummary.reduce((p, c) => p + c.AllComplete, 0),
                                AllJobTime: self.CategoriesSummary.reduce((p, c) => p + c.AllJobTime, 0),
                                TotalCount: self.CategoriesSummary.reduce((p, c) => p + c.TotalCount, 0),
                            };

                            self.CategoriesTotals = {
                                ...self.CategoriesTotals,
                                CancelledPercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.CancelledCount || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                                ClosedNonInfoPercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.ClosedNonInfoCount || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                                AllUnassignedPercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.AllUnassigned || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                                AllUnansweredPercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.AllUnanswered || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                                AllAnsweredPercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.AllAnswered || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                                AllCompletePercent:
                                    self.CategoriesTotals.TotalCount === 0
                                        ? 0
                                        : ((self.CategoriesTotals.AllComplete || 0) * 100.0) / self.CategoriesTotals.TotalCount,
                            };

                            self.ConstituentStatistics = ((self.AllStatistics && self.AllStatistics.RequestsSummary) || []).find(
                                s => s.RequestType === 'Constituent'
                            );
                            self.LastUpdated = new Date();
                            const catData = self.CategoriesSummary.map(c => {
                                return { Count: c.TotalCount, Label: c.CategoryDescription };
                            });
                            const colors = {
                                red: 'rgb(255, 99, 132)',
                                orange: 'rgb(255, 159, 64)',
                                yellow: 'rgb(255, 205, 86)',
                                green: 'rgb(75, 192, 192)',
                                blue: 'rgb(54, 162, 235)',
                                purple: 'rgb(153, 102, 255)',
                                grey: 'rgb(201, 203, 207)',
                            };
                            const colorNames = Object.keys(colors);

                            self.CategoriesChart = new Chart('categoriesCanvas', {
                                type: 'pie',
                                data: {
                                    labels: catData.map(c => c.Label),
                                    datasets: [
                                        {
                                            data: catData.map(c => c.Count),
                                            backgroundColor: catData.map((c, idx) => {
                                                const colorName = colorNames[idx % colorNames.length];
                                                return colors[colorName];
                                            }),
                                        },
                                    ],
                                },
                                options: {
                                    legend: {
                                        display: true,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Requests by Category',
                                        position: 'top',
                                    },
                                },
                            });

                            self.PrioritiesChart = new Chart('prioritiesCanvas', {
                                type: 'pie',
                                data: {
                                    labels: ['Urgent', 'High', 'Routine', 'Low', 'Pending Time Approval'],
                                    datasets: [
                                        {
                                            data: [
                                                (self.BoardStatistics && self.BoardStatistics.UrgentCount) || 0,
                                                (self.BoardStatistics && self.BoardStatistics.HighCount) || 0,
                                                (self.BoardStatistics && self.BoardStatistics.RoutineCount) || 0,
                                                (self.BoardStatistics && self.BoardStatistics.LowCount) || 0,
                                                (self.BoardStatistics && self.BoardStatistics.PendingTimeCount) || 0,
                                            ],
                                            backgroundColor: [0, 1, 2, 3, 4].map((c, idx) => {
                                                const colorName = colorNames[idx % colorNames.length];
                                                return colors[colorName];
                                            }),
                                        },
                                    ],
                                },
                                options: {
                                    legend: {
                                        display: true,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Active Requests by Priority',
                                        position: 'top',
                                    },
                                },
                            });

                            self.BoardMembersChart = new Chart('boardmembersCanvas', {
                                type: 'pie',
                                data: {
                                    labels: self.BoardMembersSummary.map(
                                        c => `${self.titleCasePipe.transform(c.FirstName)} ${self.titleCasePipe.transform(c.LastName)}`
                                    ),
                                    datasets: [
                                        {
                                            data: self.BoardMembersSummary.map(c => c.TotalCount),
                                            backgroundColor: [0, 1, 2, 3, 4].map((c, idx) => {
                                                const colorName = colorNames[idx % colorNames.length];
                                                return colors[colorName];
                                            }),
                                        },
                                    ],
                                },
                                options: {
                                    legend: {
                                        display: true,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Requests by Board Member',
                                        position: 'top',
                                    },
                                },
                            });

                            // The page is ready for viewing.
                            self.ViewReady = true;
                        },
                        error => self.errorService.HandleServiceError(error)
                    );
            },
            error => self.errorService.HandleServiceError(error)
        );
    }
}
